import { defineStore } from "pinia"
import { computed, ref } from "vue"
import { UseRootStore } from "@/model/RootStore"
import type {
	CreateSearchContextInput,
	ProjectSearchContextInput,
	SearchContext,
	SearchContextDisplayInput,
	SearchContextLayerInput,
	View as ApiView,
	UpdateSearchContextInput,
} from "@/graphql"
import { OpportunityType } from "@/graphql"
import { LayerType } from "@/model/DataModel"
import { type View, ViewScope } from "@/types"
import { deepToRaw } from "@/utils"
import * as apiClient from "@/utils/ApiClient"
import { normalizeViewFilterDefaults } from "@/views/viewFilterDefaults"
import { useViewStore } from "./viewStore"

export type SearchContextState = {
	layers: SearchContextLayerInput[]
	views: View[]
	display: SearchContextDisplayInput
}

type FetchSearchContextsOptions = {
	userProfileId?: string | null
	projectId?: string | null
	force?: boolean
}

function defaultSearchContext(): SearchContextState {
	return {
		layers: [],
		views: [],
		display: {},
	}
}

function copySearchContext(searchContext: SearchContextState): SearchContextState {
	return structuredClone(deepToRaw(searchContext))
}

const PROJECT_SEARCH_CONTEXT_VIEW_SCOPES = [
	ViewScope.OpportunitiesHuntingMap,
	ViewScope.TransactionsHuntingMap,
	ViewScope.ParcelsHuntingMap,
	ViewScope.OpportunitiesHuntingTable,
]

const EXPLORATION_SAVED_SEARCH_VIEW_SCOPES = [
	ViewScope.OpportunitiesMap,
	ViewScope.TransactionsMap,
	ViewScope.ParcelsMap,
]

const HUNTING_SAVED_SEARCH_VIEW_SCOPES = [
	ViewScope.OpportunitiesHuntingMap,
	ViewScope.TransactionsHuntingMap,
	ViewScope.ParcelsHuntingMap,
]

type ProjectSearchContextState = {
	name: string
	views: View[]
	visibleLayers: string[]
	selectedLayers: string[]
}

type CurrentSearchContextOptions = {
	name: string
	projectId?: string | null
}

const OPPORTUNITY_LAYER_TYPES = new Set<string>(Object.values(OpportunityType))

function isOpportunityLayer(layer: string): boolean {
	return OPPORTUNITY_LAYER_TYPES.has(layer)
}

function isPrimitive(value: unknown): boolean {
	return typeof value !== "object" || value === null
}

// multi-select filter values: unordered, so compare as sets
function isEqualUnorderedPrimitiveArray(a: unknown[], b: unknown[]): boolean {
	const sortedA = [...a].map(String).sort()
	const sortedB = [...b].map(String).sort()
	return sortedA.every((item, i) => item === sortedB[i])
}

// an empty value and an absent key both mean "no filter applied"
function normalizeForComparison(value: unknown): unknown {
	if (Array.isArray(value)) return value.map(normalizeForComparison)
	if (typeof value !== "object" || value === null) return value

	const output: Record<string, unknown> = {}
	for (const [key, item] of Object.entries(value)) {
		if (key === "__typename" || item == null) continue
		const normalized = normalizeForComparison(item)
		if (Array.isArray(normalized) && normalized.length === 0) continue
		if (typeof normalized === "object" && normalized !== null && Object.keys(normalized).length === 0) continue
		output[key] = normalized
	}
	return output
}

function isDeepEqual(a: unknown, b: unknown): boolean {
	if (a === b) return true
	if (typeof a !== "object" || typeof b !== "object" || a === null || b === null) return false

	if (Array.isArray(a) || Array.isArray(b)) {
		if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false
		if (a.every(isPrimitive) && b.every(isPrimitive)) return isEqualUnorderedPrimitiveArray(a, b)
		return a.every((item, i) => isDeepEqual(item, b[i]))
	}

	const aRecord = a as Record<string, unknown>
	const bRecord = b as Record<string, unknown>
	const aKeys = Object.keys(aRecord)
	const bKeys = Object.keys(bRecord)
	return aKeys.length === bKeys.length && aKeys.every(key => isDeepEqual(aRecord[key], bRecord[key]))
}

export function isSameViewState(a: unknown, b: unknown): boolean {
	return isDeepEqual(normalizeForComparison(a), normalizeForComparison(b))
}

function isEqualLayerNameSets(a: SearchContextLayerInput[], b: SearchContextLayerInput[]): boolean {
	const namesA = new Set(a.map(layer => layer.name))
	const namesB = new Set(b.map(layer => layer.name))
	return namesA.size === namesB.size && [...namesA].every(name => namesB.has(name))
}

export function projectSearchContextLayersFromState({
	visibleLayers,
	selectedLayers,
}: Pick<ProjectSearchContextState, "visibleLayers" | "selectedLayers">): SearchContextLayerInput[] {
	const layerNames = [...visibleLayers, ...selectedLayers.filter(layer => !isOpportunityLayer(layer))]
	return [...new Set(layerNames)].map(name => ({ name, active: true }))
}

export function apiViewToView(view: ApiView): View {
	const input = structuredClone(deepToRaw(view)) as Record<string, unknown>
	delete input.__typename

	return stripNullish(input) as View
}

function stripNullish(value: unknown): unknown {
	if (Array.isArray(value)) {
		return value.map(stripNullish)
	}
	if (typeof value !== "object" || value === null) {
		return value
	}

	const output: Record<string, unknown> = {}
	for (const [key, item] of Object.entries(value)) {
		if (key === "__typename" || item == null) continue
		output[key] = stripNullish(item)
	}
	return output
}

function searchContextStateFromSearchContext(searchContext: SearchContext): SearchContextState {
	return {
		layers: searchContext.layers.map(layer => stripNullish(layer) as SearchContextLayerInput),
		views: searchContext.views.map(apiViewToView),
		display: stripNullish(searchContext.display) as SearchContextDisplayInput,
	}
}

export function projectSearchContextInputFromState({
	name,
	views,
	visibleLayers,
	selectedLayers,
}: ProjectSearchContextState): ProjectSearchContextInput {
	return {
		name,
		layers: projectSearchContextLayersFromState({ visibleLayers, selectedLayers }),
		views: PROJECT_SEARCH_CONTEXT_VIEW_SCOPES.map(scope =>
			apiClient.toApiViewInput(views.find(view => view.scope === scope) ?? { scope })
		),
		display: {},
	}
}

export const useSearchContextStore = defineStore("search-context", () => {
	const rootStore = UseRootStore()
	const searchContext = ref<SearchContextState>(defaultSearchContext())
	const searchContexts = ref<SearchContext[]>([])
	const searchContextsLoaded = ref(false)
	const searchContextsLoading = ref(false)
	const searchContextsError = ref<string | null>(null)
	const activeSearchContextId = ref<string | null>(null)
	const isProjectSearchContextLoaded = ref(false)
	const personalViewScopes = ref<ViewScope[]>([])

	function replaceSearchContext(nextSearchContext: SearchContextState): void {
		searchContext.value = copySearchContext(nextSearchContext)
	}

	function resetSearchContext(): void {
		searchContext.value = defaultSearchContext()
	}

	function markProjectSearchContextLoaded(): void {
		isProjectSearchContextLoaded.value = true
	}

	function projectView(scope: ViewScope): View {
		return searchContext.value.views.find(view => view.scope === scope) ?? { scope }
	}

	function setPersonalViewScope(scope: ViewScope, isPersonal: boolean): void {
		const others = personalViewScopes.value.filter(item => item !== scope)
		personalViewScopes.value = isPersonal ? [...others, scope] : others
	}

	const isSavedSearchOnProject = computed(
		() =>
			isProjectSearchContextLoaded.value &&
			searchContexts.value.some(savedSearch => savedSearch.id === activeSearchContextId.value)
	)

	function viewsToShareWithProject(): View[] {
		const viewStore = useViewStore()
		const personalScopes = isSavedSearchOnProject.value
			? [...personalViewScopes.value, ...HUNTING_SAVED_SEARCH_VIEW_SCOPES]
			: personalViewScopes.value
		return [...viewStore.views.filter(view => !personalScopes.includes(view.scope)), ...personalScopes.map(projectView)]
	}

	function layersToShareWithProject(): SearchContextLayerInput[] {
		if (isSavedSearchOnProject.value) return searchContext.value.layers.filter(layer => layer.active)
		return projectSearchContextLayersFromState({
			visibleLayers: rootStore.visibleLayers,
			selectedLayers: rootStore.filters.layers,
		})
	}

	function currentUserProfileId(): string {
		const id = rootStore.currentUser.getInfo()?.id
		if (id == null) throw new Error("Current user id is required")
		return String(id)
	}

	function savedSearchViewScopes(projectId?: string | null): ViewScope[] {
		return projectId ? HUNTING_SAVED_SEARCH_VIEW_SCOPES : EXPLORATION_SAVED_SEARCH_VIEW_SCOPES
	}

	function searchContextInputFromCurrentSearch({
		name,
		projectId = null,
	}: CurrentSearchContextOptions): CreateSearchContextInput {
		const viewStore = useViewStore()
		return {
			name,
			userProfileId: currentUserProfileId(),
			projectId,
			layers: projectSearchContextLayersFromState({
				visibleLayers: rootStore.visibleLayers,
				selectedLayers: rootStore.filters.layers,
			}),
			views: savedSearchViewScopes(projectId).map(scope =>
				apiClient.toApiViewInput(viewStore.views.find(view => view.scope === scope) ?? { scope })
			),
			display: {},
		}
	}

	function applyLayers(layers: SearchContextLayerInput[]): void {
		const activeLayers = layers.filter(layer => layer.active).map(layer => layer.name)
		rootStore.setVisibleLayers(
			activeLayers.filter(layer => !isOpportunityLayer(layer) && layer !== LayerType.Transaction)
		)

		const typedLayers = new Set(rootStore.filters.layers)
		if (activeLayers.includes(LayerType.Transaction)) {
			typedLayers.add(LayerType.Transaction)
		} else {
			typedLayers.delete(LayerType.Transaction)
		}
		rootStore.filters.layers = [...typedLayers]
	}

	function applySearchContext(nextSearchContext: SearchContext): void {
		const state = searchContextStateFromSearchContext(nextSearchContext)
		replaceSearchContext(state)
		useViewStore().replaceViews(state.views)
		applyLayers(state.layers)
		activeSearchContextId.value = nextSearchContext.id
	}

	function applySavedSearchContext(savedSearch: SearchContext, projectId: string | null): void {
		const state = searchContextStateFromSearchContext(savedSearch)
		useViewStore().replaceScopedViews(savedSearchViewScopes(projectId), state.views)
		applyLayers(state.layers)
		activeSearchContextId.value = savedSearch.id
	}

	function resetToBaseSearch(projectId: string | null): void {
		const scopes = savedSearchViewScopes(projectId)
		const isOnProject = projectId != null && isProjectSearchContextLoaded.value
		useViewStore().replaceScopedViews(scopes, isOnProject ? scopes.map(projectView) : [])
		if (!isOnProject) return

		if (searchContext.value.layers.length === 0) {
			rootStore.setVisibleLayers(rootStore.defaultVisibleLayers)
		} else {
			applyLayers(searchContext.value.layers)
		}
	}

	function clearActiveSearchContext(): void {
		activeSearchContextId.value = null
	}

	const isProjectSearchContextDirty = computed(() => {
		if (!isProjectSearchContextLoaded.value) return false

		if (
			!isEqualLayerNameSets(
				layersToShareWithProject(),
				searchContext.value.layers.filter(layer => layer.active)
			)
		) {
			return true
		}

		const views = viewsToShareWithProject()
		return PROJECT_SEARCH_CONTEXT_VIEW_SCOPES.some(scope => {
			const currentView = apiClient.toApiViewInput(
				normalizeViewFilterDefaults(views.find(view => view.scope === scope) ?? { scope })
			)
			const savedView = apiClient.toApiViewInput(normalizeViewFilterDefaults(projectView(scope)))
			return !isSameViewState(currentView, savedView)
		})
	})

	async function fetchSearchContexts(options: FetchSearchContextsOptions = {}): Promise<SearchContext[]> {
		if (searchContextsLoaded.value && !options.force) return searchContexts.value

		searchContextsLoading.value = true
		searchContextsError.value = null
		try {
			searchContexts.value = await apiClient.fetchSearchContexts(rootStore.userApiToken, {
				userProfileId: options.userProfileId,
				projectId: options.projectId,
			})
			searchContextsLoaded.value = true
			return searchContexts.value
		} catch (error) {
			searchContextsError.value = error instanceof Error ? error.message : String(error)
			throw error
		} finally {
			searchContextsLoading.value = false
		}
	}

	async function fetchSearchContext(id: string): Promise<SearchContext | null> {
		return await apiClient.fetchSearchContext(rootStore.userApiToken, id)
	}

	async function createSearchContext(input: CreateSearchContextInput): Promise<SearchContext> {
		const createdSearchContext = await apiClient.createSearchContext(rootStore.userApiToken, input)
		searchContexts.value = [
			createdSearchContext,
			...searchContexts.value.filter(searchContext => searchContext.id !== createdSearchContext.id),
		]
		searchContextsLoaded.value = true
		return createdSearchContext
	}

	async function createSearchContextFromCurrentSearch(options: CurrentSearchContextOptions): Promise<SearchContext> {
		const createdSearchContext = await createSearchContext(searchContextInputFromCurrentSearch(options))
		activeSearchContextId.value = createdSearchContext.id
		return createdSearchContext
	}

	async function updateSearchContext(id: string, input: UpdateSearchContextInput): Promise<SearchContext> {
		const updatedSearchContext = await apiClient.updateSearchContext(rootStore.userApiToken, id, input)
		searchContexts.value = searchContexts.value.map(searchContext =>
			searchContext.id === updatedSearchContext.id ? updatedSearchContext : searchContext
		)
		return updatedSearchContext
	}

	async function updateSearchContextFromCurrentSearch(
		id: string,
		options: Omit<CurrentSearchContextOptions, "name"> & { name?: string }
	): Promise<SearchContext> {
		const existingSearchContext = searchContexts.value.find(searchContext => searchContext.id === id)
		const name = options.name ?? existingSearchContext?.name
		if (!name) throw new Error("Search context name is required")

		const input = searchContextInputFromCurrentSearch({ ...options, name })
		const updatedSearchContext = await updateSearchContext(id, {
			name: input.name,
			projectId: input.projectId,
			layers: input.layers,
			views: input.views,
			display: input.display,
		})
		activeSearchContextId.value = updatedSearchContext.id
		return updatedSearchContext
	}

	async function updateSearchContextName(id: string, name: string): Promise<SearchContext> {
		return await updateSearchContext(id, { name })
	}

	async function deleteSearchContext(id: string): Promise<boolean> {
		const deleted = await apiClient.deleteSearchContext(rootStore.userApiToken, id)
		if (deleted) {
			searchContexts.value = searchContexts.value.filter(searchContext => searchContext.id !== id)
			if (activeSearchContextId.value === id) {
				activeSearchContextId.value = null
			}
		}
		return deleted
	}

	async function updateProjectSearchContext(projectId: string, name: string): Promise<SearchContext> {
		const updatedSearchContext = await apiClient.updateProjectSearchContext(rootStore.userApiToken, projectId, {
			...projectSearchContextInputFromState({
				name,
				views: viewsToShareWithProject(),
				visibleLayers: rootStore.visibleLayers,
				selectedLayers: rootStore.filters.layers,
			}),
			layers: layersToShareWithProject(),
		})
		replaceSearchContext(searchContextStateFromSearchContext(updatedSearchContext))
		return updatedSearchContext
	}

	return {
		searchContext,
		searchContexts,
		searchContextsLoaded,
		searchContextsLoading,
		searchContextsError,
		activeSearchContextId,
		isProjectSearchContextDirty,
		isProjectSearchContextLoaded,
		markProjectSearchContextLoaded,
		projectView,
		setPersonalViewScope,
		replaceSearchContext,
		resetSearchContext,
		clearActiveSearchContext,
		applySearchContext,
		applySavedSearchContext,
		resetToBaseSearch,
		searchContextInputFromCurrentSearch,
		fetchSearchContexts,
		fetchSearchContext,
		createSearchContext,
		createSearchContextFromCurrentSearch,
		updateSearchContext,
		updateSearchContextFromCurrentSearch,
		updateSearchContextName,
		deleteSearchContext,
		updateProjectSearchContext,
	}
})
